import React from 'react';
import { Box, Typography } from '@mui/material';

import Description from 'components/CareSummary/Description';
import Empty from 'components/CareSummary/Empty';
import Title from 'components/CareSummary/Title';
import { Container } from 'shared-components/Container';
import { useGetProfileParticipantTasksQuery, useGetProfileParticipantTaskQuery } from 'store/api/pkd/endpoints/participantTask';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { TaskTitles } from 'utils/enum';
import { ChartTextSkeleton } from '../ChartSkeleton';
import { getFamilyIntracranial } from './utils';

const FamilyHistoryOfAneurysms = () => {
  const { isPersonLoading, isPersonAttributesFetching, person, personId, personAccountProfileId } =
    useGetPersonAndAttributes();

  const { participantTasks, isParticipantTaskLoading } =
    useGetProfileParticipantTasksQuery(
      { personAccountProfileId },
      { skip             : !personAccountProfileId ,
        selectFromResult : ({ data = [], isFetching }) => ({
          participantTasks         : data,
          isParticipantTaskLoading : isFetching,
        }),
      }
    );
    
  const coreQuestionnaireTaskId = participantTasks?.find(item => item.taskTitle === TaskTitles.coreQuestionnaire)?.id;

  const { data:coreQuestionnaire, isLoading:isCoreQuestionnaireLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: coreQuestionnaireTaskId },
      { skip: !coreQuestionnaireTaskId }
    );
  const coreQuestionnaireData = coreQuestionnaire;

  const familyIntracranialTaskId = participantTasks?.find(item => item.taskTitle === TaskTitles.vascularOutcomesGeneral)?.id;

  const { data:familyIntracranial, isLoading:isFamilyIntracranialLoading } =
    useGetProfileParticipantTaskQuery(
      { personAccountProfileId, participantTaskId: participantTasks?.find(item => item.taskTitle === TaskTitles.vascularOutcomesGeneral)?.id },
      { skip: !familyIntracranialTaskId }
    );

  const familyIntracranialEntries = getFamilyIntracranial(familyIntracranial);

  const isLoading =
    isPersonLoading ||
    isParticipantTaskLoading ||
    isFamilyIntracranialLoading ||
    isCoreQuestionnaireLoading;

  const content = isLoading
    ? (
      <ChartTextSkeleton />
    )
    : familyIntracranialEntries !== ''
      ? (
        <Box
          m='20px 0px'>
          <Typography
            variant='pl1' mb={1}>
        Family history of intracranial aneurysms: {familyIntracranialEntries}
          </Typography>
        </Box>
      )
      : (
        <Empty
          description='Fill out the Vascular Outcomes module'
          to={`/questionnaire/${familyIntracranial?.participantTaskId}`}
          buttonText='Fill out the Vascular Outcomes module'
          hideAction={
            !coreQuestionnaireData?.taskEntry ||
        (Boolean(coreQuestionnaireData?.taskEntry) &&
          familyIntracranialEntries?.length == 0)
          }
        />
      );

  return (
    <Container>
      <Title>Family history of aneurysms</Title>
      <Description>
        {`ADPKD patients have a 5–10% risk of developing intracranial
      (brain) aneurysms (~5x the risk of the general population). Having
      a family history of aneurysms can also increase your risk (another
      ~2-3x compared to ADPKD patients without a family history!). Talk
      to your doctor about whether you should be screened.`}
      </Description>
      {content}
    </Container>
  );
};

export default FamilyHistoryOfAneurysms;
